/**
 * TanStack Query client configuration for room booking management
 */

import { QueryClient } from "@tanstack/vue-query";

/**
 * Default query options for the application
 */
const defaultQueryOptions = {
	queries: {
		// Global defaults
		staleTime: 1000 * 60 * 5, // 5 minutes default
		gcTime: 1000 * 60 * 30, // 30 minutes default (previously cacheTime)
		retry: 3,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

		// Network mode - always try to fetch if online
		networkMode: "online",

		// Refetch settings
		refetchOnWindowFocus: false,
		refetchOnReconnect: true,
		refetchOnMount: true,
	},
	mutations: {
		// Global mutation defaults
		retry: 0,
		networkMode: "online",
	},
};

/**
 * Error handler for queries
 * @param {Error} error - The error that occurred
 * @param {Object} query - The query that failed
 */
const handleQueryError = (error, query) => {
	console.error("Query error:", {
		queryKey: query.queryKey,
		error: error.message,
		stack: error.stack,
	});

	// You could integrate with error reporting service here
	// e.g., Sentry.captureException(error, { tags: { queryKey: query.queryKey } });
};

/**
 * Error handler for mutations
 * @param {Error} error - The error that occurred
 * @param {*} variables - The variables passed to the mutation
 * @param {*} context - The context from onMutate
 * @param {Object} mutation - The mutation that failed
 */
const handleMutationError = (error, variables, context, mutation) => {
	console.error("Mutation error:", {
		mutationKey: mutation.options.mutationKey,
		error: error.message,
		variables,
		stack: error.stack,
	});

	// You could integrate with error reporting service here
	// e.g., Sentry.captureException(error, { tags: { mutationKey: mutation.options.mutationKey } });
};

/**
 * Create and configure QueryClient instance
 * @param {Object} [customOptions] - Custom options to override defaults
 * @returns {QueryClient} Configured QueryClient instance
 */
export const createQueryClient = (customOptions = {}) => {
	return new QueryClient({
		defaultOptions: {
			...defaultQueryOptions,
			...customOptions,
		},
	});
};

/**
 * Pre-configured QueryClient instance with optimized defaults
 * for room booking management
 */
export const roomBookingQueryClient = createQueryClient({
	queries: {
		...defaultQueryOptions.queries,
		// Shorter stale time for booking-related queries
		staleTime: 1000 * 60 * 2, // 2 minutes
		gcTime: 1000 * 60 * 15, // 15 minutes
	},
});

/**
 * Development mode query client with more verbose logging
 */
export const createDevQueryClient = () => {
	return createQueryClient({
		queries: {
			...defaultQueryOptions.queries,
			// More frequent refetching in development
			staleTime: 1000 * 30, // 30 seconds
			gcTime: 1000 * 60 * 5, // 5 minutes
		},
	});
};

export default roomBookingQueryClient;
